// ProviderScreen.js

import axios from "axios";
import React, { useState, useEffect } from "react";
import { API_IP } from "../env";
import ServicesOfProvidersFrom from "./Form/ServicesOfProvidersForm";
import NewProviderForm from "./Form/NewProviderForm";
import NewProviderRequestForm from "./Form/NewProviderRequestForm";
import ProviderDetailForm from "./Form/ProviderDetailForm";
import AskingForm from "./Form/AskingForm";

// Import react-bootstrap components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table"; // Already here, good.

function ProviderScreen() {
  const [refreshProvider, setRefreshProvider] = useState(false);
  const [providerData, SetProviderData] = useState([]);

  // State for which modal to show
  const [showServicesForm, setShowServicesForm] = useState(false);
  const [showNewProviderForm, setShowNewProviderForm] = useState(false);
  const [showRequestsForm, setShowRequestsForm] = useState(false);
  const [showDetailForm, setShowDetailForm] = useState(false);
  const [showAskingForm, setShowAskingForm] = useState(false);

  // Data for the modals
  const [selectedProviderId, setSelectedProviderId] = useState();
  const [selectedProviderName, setSelectedProviderName] = useState();

  const [newRequestCount, setRequestCount] = useState(0);

  // Use a string for the filter state, it's clearer
  const [filter, setFilter] = useState("verified"); // 'verified' or 'unverified'

  // Standardized API call function
  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    const reqConfig = {
      url: url,
      method: method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: data,
    };
    return await axios(reqConfig);
  }

  // Fetch all providers
  async function getAllProviders() {
    const { data } = await axiosReqConfig(
      `${API_IP}/api/admin/providers`,
      "get",
      []
    );
    SetProviderData(data);
  }

  // Fetch new request count separately
  async function fetchRequestCount() {
    try {
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/providers/requests`,
        "get",
        []
      );
      setRequestCount(data.length);
    } catch (err) {
      console.error("Could not fetch request count", err);
    }
  }

  useEffect(() => {
    getAllProviders();
    fetchRequestCount();
  }, [refreshProvider]);

  // --- Modal Open/Close Handlers ---
  const openDetailModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowDetailForm(true);
  };

  const openServicesModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowServicesForm(true);
  };

  const openDeleteModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowAskingForm(true);
  };

  // --- Render Item ---
  function Item({ item }) {
    // Determine if the item should be shown based on the filter
    const isVerified = item.verification === true && item.deactive === false;
    const isUnverified = item.verification === false && item.deactive === false;

    let show = false;
    if (filter === "verified" && isVerified) show = true;
    if (filter === "unverified" && isUnverified) show = true;

    if (!show) return null;

    return (
      <tr>
        <td>{item.provider_id}</td>
        <td>
          {item.provider_name}
          {item.verification ? (
            <Badge bg="success" className="ms-2">
              Verified
            </Badge>
          ) : (
            <Badge bg="warning" text="dark" className="ms-2">
              Unverified
            </Badge>
          )}
        </td>
        <td>
          <Button
            variant="info"
            size="sm"
            onClick={() => openDetailModal(item)}
          >
            Details
          </Button>
        </td>
        <td>
          <Button
            variant="info"
            size="sm"
            onClick={() => openServicesModal(item)}
          >
            View Services
          </Button>
        </td>
        <td>
          <Button
            variant="danger"
            size="sm"
            onClick={() => openDeleteModal(item)}
          >
            Delete
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <>
      <Container fluid className="my-4">
        {/* Header Row */}
        <Row className="mb-3 p-3 bg-light rounded-3 align-items-center">
          <Col>
            <ButtonGroup>
              <Button
                variant={filter === "verified" ? "primary" : "outline-primary"}
                onClick={() => setFilter("verified")}
              >
                Verified Providers
              </Button>
              <Button
                variant={
                  filter === "unverified" ? "warning" : "outline-warning"
                }
                onClick={() => setFilter("unverified")}
              >
                Unverified Providers
              </Button>
            </ButtonGroup>
          </Col>
          <Col xs="auto">
            <Button variant="success" onClick={() => setShowRequestsForm(true)}>
              New Requests <Badge bg="danger">{newRequestCount}</Badge>
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant="success"
              onClick={() => setShowNewProviderForm(true)}
            >
              + Add New Provider
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Row>
          <Col>
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>Provider ID</th>
                  <th>Provider Name</th>
                  <th>Information</th>
                  <th>List Of Services</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providerData.map((item) => (
                  <Item key={item.provider_id} item={item} />
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>

      {/* --- Modals --- */}
      {/* These now use the 'show' and 'onHide' props */}

      <AskingForm
        show={showAskingForm}
        onHide={() => setShowAskingForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />

      <ServicesOfProvidersFrom
        show={showServicesForm}
        onHide={() => setShowServicesForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
      />

      <NewProviderForm
        show={showNewProviderForm}
        onHide={() => setShowNewProviderForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />

      <NewProviderRequestForm
        show={showRequestsForm}
        onHide={() => setShowRequestsForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />

      <ProviderDetailForm
        show={showDetailForm}
        onHide={() => setShowDetailForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
    </>
  );
}

export default ProviderScreen;
